import type { RouteObject } from "react-router-dom";
import { Navigate } from "react-router-dom";
import NotFound from "@/pages/NotFound";
import LoginPage from "@/pages/login/page";
import ForgotPasswordPage from "@/pages/forgot-password/page";
import ResetPasswordPage from "@/pages/reset-password/page";
import StatementPage from "@/pages/statement/page";
import DashboardPage from "@/pages/dashboard/page";
import SalesPage from "@/pages/sales/page";
import RestaurantPosPage from "@/pages/restaurant-pos/page";
import ProductsPage from "@/pages/products/page";
import CategoriesPage from "@/pages/categories/page";
import SuppliersPage from "@/pages/suppliers/page";
import DealersPage from "@/pages/dealers/page";
import ReportsPage from "@/pages/reports/page";
import FinancialReportsPage from "@/pages/reports/financial/page";
import StockReportsPage from "@/pages/reports/stock/page";
import CustomerListPage from "@/pages/reports/customers/page";
import SalesPurchasesReportsPage from "@/pages/reports/sales-purchases/page";
import RestaurantSalesReportPage from "@/pages/reports/restaurant/page";
import RestaurantBranchesPage from "@/pages/restaurant/branches/page";
import RestaurantStockPage from "@/pages/restaurant/stock/page";
import InvoicesPage from "@/pages/invoices/page";
import AccountingPage from "@/pages/accounting/page";
import SupportPage from "@/pages/support/page";
import BackupPage from "@/pages/backup/page";
import PaymentNotificationsPage from "@/pages/payment-notifications/page";
import B2BLayout from "@/components/feature/B2BLayout";
import AuthGuard from "@/components/feature/AuthGuard";
import { useAuth } from "@/contexts/AuthContext";

import AdminLoginPage from "@/pages/admin/login/page";
import AdminLayout from "@/pages/admin/components/AdminLayout";
import AdminDashboardPage from "@/pages/admin/dashboard/page";
import AdminUsersPage from "@/pages/admin/users/page";
import AdminDealersPage from "@/pages/admin/dealers/page";
import AdminDealerCurrenciesPage from "@/pages/admin/dealer-currencies/page";
import AdminDealerUsersPage from "@/pages/admin/dealer-users/page";
import AdminDealerLogsPage from "@/pages/admin/dealer-logs/page";
import AdminDataImportPage from "@/pages/admin/data-import/page";
import AdminSupportPage from "@/pages/admin/support/page";
import AdminBankAccountsPage from "@/pages/admin/bank-accounts/page";
import AdminProductsPage from "@/pages/admin/products/page";
import AdminCategoriesPage from "@/pages/admin/categories/page";
import AdminSettingsPage from "@/pages/admin/settings/page";
import AdminDealerAdminsPage from "@/pages/admin/dealer-admins/page";

import DealerAdminCurrenciesPage from "@/pages/dealer-admin/currencies/page";
import DealerAdminUsersPage from "@/pages/dealer-admin/users/page";
import DealerAdminLogsPage from "@/pages/dealer-admin/logs/page";
import DealerAdminBankAccountsPage from "@/pages/dealer-admin/bank-accounts/page";
import DealerAdminImportPage from "@/pages/dealer-admin/import/page";
import RestaurantSalesImportPage from "@/pages/restaurant/import-sales/page";
import DealerAdminBranchesPage from "@/pages/dealer-admin/branches/page";
import DealerAdminStaffPage from "@/pages/dealer-admin/staff/page";
import StockImportPage from "@/pages/dealer-admin/stock-import/page";
import PaymentSettingsPage from "@/pages/dealer-admin/payment-settings/page";
import WarehousePosPage from "@/pages/warehouse/pos/page";
import WarehouseStockPage from "@/pages/warehouse/stock/page";
import WarehouseStockReportPage from "@/pages/warehouse/reports/stock/page";
import WarehouseSalesReportPage from "@/pages/warehouse/reports/sales/page";
import WarehouseSetupPage from "@/pages/warehouse/setup/page";
import ModelControlPage from "@/pages/warehouse/model-control/page";
import WarehouseCrmPage from "@/pages/warehouse/crm/page";

function HomeRedirect() {
  const { user } = useAuth();
  const isAvullar = user?.dealerName?.toLowerCase().includes("avullar") || false;
  const isMG = !user?.loginType?.includes("admin") && (
    user?.dealerCode?.toLowerCase() === "mg" ||
    user?.dealerName?.toLowerCase().includes("mg teknik") ||
    user?.dealerName?.toLowerCase() === "mg"
  );
  if (isMG) return <Navigate to="/warehouse/pos" replace />;
  if (isAvullar) return <Navigate to="/restaurant-pos" replace />;
  if (user?.loginType === "dealer_admin") return <Navigate to="/dealer-admin/currencies" replace />;
  return <Navigate to="/dashboard" replace />;
}

function DashboardGuard() {
  const { user } = useAuth();
  const isAvullar = user?.dealerName?.toLowerCase().includes("avullar") || false;
  const isMG = !user?.loginType?.includes("admin") && (
    user?.dealerCode?.toLowerCase() === "mg" ||
    user?.dealerName?.toLowerCase().includes("mg teknik") ||
    user?.dealerName?.toLowerCase() === "mg"
  );
  if (isMG) return <Navigate to="/warehouse/pos" replace />;
  if (isAvullar) return <Navigate to="/restaurant-pos" replace />;
  return <DashboardPage />;
}

function RestaurantImportSalesGuard() {
  const { user } = useAuth();
  if (user?.loginType !== "dealer_admin") {
    return <Navigate to="/restaurant-pos" replace />;
  }
  return <RestaurantSalesImportPage />;
}

const routes: RouteObject[] = [
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    path: "/forgot-password",
    element: <ForgotPasswordPage />,
  },
  {
    path: "/reset-password",
    element: <ResetPasswordPage />,
  },
  {
    path: "/statement/:token",
    element: <StatementPage />,
  },
  {
    path: "/",
    element: (
      <AuthGuard>
        <B2BLayout />
      </AuthGuard>
    ),
    children: [
      { index: true, element: <HomeRedirect /> },
      { path: "dashboard", element: <DashboardGuard /> },
      { path: "sales", element: <SalesPage /> },
      { path: "restaurant-pos", element: <RestaurantPosPage /> },
      { path: "products", element: <ProductsPage /> },
      { path: "categories", element: <CategoriesPage /> },
      { path: "suppliers", element: <SuppliersPage /> },
      { path: "dealers", element: <DealersPage /> },
      { path: "reports", element: <ReportsPage /> },
      { path: "reports/sales-purchases", element: <SalesPurchasesReportsPage /> },
      { path: "reports/financial", element: <FinancialReportsPage /> },
      { path: "reports/stock", element: <StockReportsPage /> },
      { path: "reports/customers", element: <CustomerListPage /> },
      { path: "reports/restaurant", element: <RestaurantSalesReportPage /> },
      { path: "restaurant/branches", element: <RestaurantBranchesPage /> },
      { path: "restaurant/stock", element: <RestaurantStockPage /> },
      { path: "restaurant/import-sales", element: <RestaurantImportSalesGuard /> },
      { path: "invoices", element: <InvoicesPage /> },
      { path: "accounting", element: <AccountingPage /> },
      { path: "support", element: <SupportPage /> },
      { path: "payment-notifications", element: <PaymentNotificationsPage /> },
      { path: "backup", element: <BackupPage /> },
      { path: "dealer-admin/currencies", element: <DealerAdminCurrenciesPage /> },
      { path: "dealer-admin/users", element: <DealerAdminUsersPage /> },
      { path: "dealer-admin/branches", element: <DealerAdminBranchesPage /> },
      { path: "dealer-admin/staff", element: <DealerAdminStaffPage /> },
      { path: "dealer-admin/logs", element: <DealerAdminLogsPage /> },
      { path: "dealer-admin/bank-accounts", element: <DealerAdminBankAccountsPage /> },
      { path: "dealer-admin/import", element: <DealerAdminImportPage /> },
      { path: "dealer-admin/stock-import",     element: <StockImportPage /> },
      { path: "dealer-admin/payment-settings", element: <PaymentSettingsPage /> },
      { path: "warehouse/pos",             element: <WarehousePosPage /> },
      { path: "warehouse/stock",           element: <WarehouseStockPage /> },
      { path: "warehouse/reports/stock",   element: <WarehouseStockReportPage /> },
      { path: "warehouse/reports/sales",   element: <WarehouseSalesReportPage /> },
      { path: "warehouse/setup",            element: <WarehouseSetupPage /> },
      { path: "warehouse/model-control",    element: <ModelControlPage /> },
      { path: "warehouse/crm",              element: <WarehouseCrmPage /> },
    ],
  },
  {
    path: "/admin/login",
    element: <AdminLoginPage />,
  },
  {
    path: "/admin",
    element: <AdminLayout />,
    children: [
      { path: "dashboard", element: <AdminDashboardPage /> },
      { path: "users", element: <AdminUsersPage /> },
      { path: "dealers", element: <AdminDealersPage /> },
      { path: "dealer-admins", element: <AdminDealerAdminsPage /> },
      { path: "dealer-currencies", element: <AdminDealerCurrenciesPage /> },
      { path: "dealer-users", element: <AdminDealerUsersPage /> },
      { path: "dealer-logs", element: <AdminDealerLogsPage /> },
      { path: "data-import", element: <AdminDataImportPage /> },
      { path: "support", element: <AdminSupportPage /> },
      { path: "bank-accounts", element: <AdminBankAccountsPage /> },
      { path: "products", element: <AdminProductsPage /> },
      { path: "categories", element: <AdminCategoriesPage /> },
      { path: "settings", element: <AdminSettingsPage /> },
    ],
  },
  {
    path: "*",
    element: <NotFound />,
  },
];

export default routes;